<script setup>
/**
 * @description 基础轮播组件，支持鼠标拖拽和导航按钮。
 * @param {boolean} showNav - 是否显示导航按钮。默认为 true.
 */

// 1. Imports
import { IconLeft, IconRight } from '@arco-design/web-vue/es/icon'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useMediaQuery } from '@/composables/useMediaQuery'

// 2. Props
const props = defineProps({
  /**
   * @description 是否显示导航
   * @type {boolean}
   */
  showNav: {
    type: Boolean,
    default: true,
  },

  /**
   * @description 是否在可滚动时常驻显示导航按钮；默认保持 hover 展示。
   * @type {boolean}
   */
  alwaysShowNav: {
    type: Boolean,
    default: false,
  },

  size: {
    type: String,
    default: 'default',
    validator: (value) => {
      return ['default', 'large'].includes(value)
    },
  },

  /**
   * @description 网格间距
   * @type {number}
   */
  gap: {
    type: Number,
    default: 16,
  },

  /**
   * @description 移动端网格间距；不传时保持组件默认 12px
   * @type {number}
   */
  mobileGap: {
    type: Number,
    default: 12,
  },

  /**
   * @description 顶部内边距（设置此值，激活时顶部才不会被遮挡）
   * @type {number}
   */
  paddingTop: {
    type: Number,
    default: 12,
  },

  /**
   * @description 底部内边距（设置此值，激活时底部阴影才不会被遮挡）
   * @type {number}
   */
  paddingBottom: {
    type: Number,
    default: 0,
  },

  /**
   * @description 右侧内边距（用于横向滚动内容末尾留白）
   * @type {number}
   */
  paddingRight: {
    type: Number,
    default: 0,
  },

  /**
   * @description 是否启用全屏左右滚动
   * @type {boolean}
   */
  fullWidthScroll: {
    type: Boolean,
    default: false,
  },

  /**
   * @description 左侧切换按钮距左边的距离（px），不传则使用默认样式
   * @type {number|undefined}
   */
  navLeft: {
    type: Number,
    default: undefined,
  },

  /**
   * @description 右侧切换按钮距右边的距离（px），不传则使用默认样式
   * @type {number|undefined}
   */
  navRight: {
    type: Number,
    default: undefined,
  },

  /**
   * @description 背景色（如 '#fff'、'white'），不传则使用默认
   * @type {string}
   */
  background: {
    type: String,
    default: '',
  },

  /**
   * @description 边缘渐变蒙层颜色；传入时优先于根据容器背景色自动推断
   * @type {string}
   */
  overlayColor: {
    type: String,
    default: '',
  },

  /**
   * @description 边缘渐变蒙层的完整可见宽度；数字按 px 处理，也支持任意 CSS 长度值
   * @type {string|number}
   */
  overlayWidth: {
    type: [String, Number],
    default: '',
  },

  /**
   * @description 当内容总宽度未超出容器时，主轴居中对齐；溢出时仍为起始对齐以保证横向滚动体验
   * @type {boolean}
   */
  centerWhenNotOverflowing: {
    type: Boolean,
    default: false,
  },

  /**
   * @description 是否显示可拖拽的自定义横向滚动条
   * @type {boolean}
   */
  showScrollbar: {
    type: Boolean,
    default: false,
  },

  /**
   * @description 内容未溢出时是否仍显示自定义横向滚动条，可作为固定分割线使用
   * @type {boolean}
   */
  alwaysShowScrollbar: {
    type: Boolean,
    default: false,
  },

  /**
   * @description 自定义横向滚动条宽度，支持 CSS 长度值
   * @type {string}
   */
  scrollbarWidth: {
    type: String,
    default: '100%',
  },

  /**
   * @description 自定义横向滚动条滑块宽度，留空时按可视比例自动计算
   * @type {string}
   */
  scrollbarThumbWidth: {
    type: String,
    default: '',
  },
})

// 3. Emits

// 4. Refs/Reactive State
const { isMobile } = useMediaQuery()
const effectiveGap = computed(() => (isMobile.value ? props.mobileGap : props.gap))
const carouselRoot = ref(null)
const scrollContainer = ref(null)
const scrollbarTrack = ref(null)
const isDragging = ref(false)
const isScrollbarDragging = ref(false)
const showButtons = ref(false)
const isOverflowing = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const overlayColorRgb = ref('255, 255, 255')
const overlayColorAlpha = ref(0.5)
const itemWidth = ref(0)
const scrollbarThumbPixelWidth = ref(0)
const scrollbarThumbLeft = ref(0)
const edgeHoverLeftWidth = ref(0)
const edgeHoverRightWidth = ref(0)
const isViewportCentered = ref(false)
const startX = ref(0)
const scrollLeftState = ref(0)
const lastX = ref(0)
const velocity = ref(0)
const shouldShowCustomScrollbar = computed(
  () => props.showScrollbar && (isOverflowing.value || props.alwaysShowScrollbar)
)
const shouldExtendHoverArea = computed(
  () => props.showNav && !isMobile.value && isOverflowing.value && isViewportCentered.value
)
let rafId = null
let edgeHoverResizeRafId = null
let resizeObserver = null
let mutationObserver = null
let startPageX = 0
let scrollbarDragStartX = 0
let scrollbarDragStartLeft = 0
const DRAG_CLICK_THRESHOLD = 5

// 5. Methods
/**
 * @description 根据横向滚动位置同步自定义滚动条
 * @returns {void}
 */
const updateCustomScrollbar = () => {
  if (!scrollContainer.value || !scrollbarTrack.value) return

  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value
  const trackWidth = scrollbarTrack.value.clientWidth
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
  const visibleRatio = scrollWidth > 0 ? clientWidth / scrollWidth : 1
  const customThumbWidth = props.scrollbarThumbWidth
    ? Number.parseFloat(props.scrollbarThumbWidth)
    : 0
  const thumbWidth =
    customThumbWidth > 0 ? customThumbWidth : Math.max(trackWidth * visibleRatio, 48)
  const maxThumbLeft = Math.max(0, trackWidth - thumbWidth)

  scrollbarThumbPixelWidth.value = Math.min(trackWidth, thumbWidth)
  scrollbarThumbLeft.value =
    maxScrollLeft > 0 ? Math.min(maxThumbLeft, (scrollLeft / maxScrollLeft) * maxThumbLeft) : 0
}

/**
 * @description 根据当前滚动位置更新左右是否可滚动
 */
const updateScrollAvailability = () => {
  if (!scrollContainer.value) return
  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
  const threshold = 2
  canScrollLeft.value = scrollLeft > threshold
  canScrollRight.value = scrollLeft < maxScrollLeft - threshold
  updateCustomScrollbar()
}

/**
 * @description 将居中轮播两侧到视口边缘的空白纳入悬浮命中区
 * @reason 用户从页面边缘移向外置导航按钮时，按钮应连续显示且不阻断页面其他区域
 * @returns {void}
 */
const updateEdgeHoverArea = () => {
  if (!import.meta.client || !carouselRoot.value) return

  const rootRect = carouselRoot.value.getBoundingClientRect()
  const viewportWidth = document.documentElement.clientWidth
  const leftSpace = Math.max(0, rootRect.left)
  const rightSpace = Math.max(0, viewportWidth - rootRect.right)
  const centerTolerance = 2

  isViewportCentered.value = Math.abs(leftSpace - rightSpace) <= centerTolerance
  edgeHoverLeftWidth.value = isViewportCentered.value ? leftSpace : 0
  edgeHoverRightWidth.value = isViewportCentered.value ? rightSpace : 0
}

/**
 * @description 窗口缩放时按下一帧更新两侧悬浮命中区
 * @returns {void}
 */
const handleViewportResize = () => {
  if (edgeHoverResizeRafId) cancelAnimationFrame(edgeHoverResizeRafId)
  edgeHoverResizeRafId = requestAnimationFrame(() => {
    edgeHoverResizeRafId = null
    updateEdgeHoverArea()
  })
}

/**
 * @description 检查内容是否溢出并计算子元素宽度
 */
const updateCarouselState = () => {
  if (scrollContainer.value) {
    const { scrollWidth, clientWidth } = scrollContainer.value
    isOverflowing.value = scrollWidth > clientWidth

    const firstChild = scrollContainer.value.firstElementChild
    if (firstChild) {
      itemWidth.value = firstChild.offsetWidth
    }
    updateScrollAvailability()
  }
  updateEdgeHoverArea()
}

/**
 * @description 处理轮播滚动，统一同步导航与滚动条状态
 * @returns {void}
 */
const handleScroll = () => {
  updateScrollAvailability()
}

/**
 * @description 将 CSS 颜色解析为 rgba() 所需的 RGB 通道和可选透明度
 * @param {string} color
 * @returns {{ rgb: string, alpha: number|null }|null}
 */
const resolveColor = (color) => {
  if (!color || !carouselRoot.value) return null

  const colorProbe = document.createElement('span')
  colorProbe.style.color = color
  if (!colorProbe.style.color) return null

  colorProbe.style.display = 'none'
  carouselRoot.value.appendChild(colorProbe)
  const resolvedColor = window.getComputedStyle(colorProbe).color
  colorProbe.remove()

  const colorParts = resolvedColor.match(/\d+(\.\d+)?/g)
  if (!colorParts || colorParts.length < 3) return null

  return {
    rgb: colorParts.slice(0, 3).join(', '),
    alpha: colorParts.length >= 4 ? Number(colorParts[3]) : null,
  }
}

/**
 * @description 同步边缘渐变蒙层颜色，优先使用指定颜色，否则取当前容器或最近父级的非透明背景
 */
const syncOverlayColor = () => {
  if (!import.meta.client || !carouselRoot.value) return

  const forcedColor = resolveColor(props.overlayColor)
  if (forcedColor) {
    overlayColorRgb.value = forcedColor.rgb
    overlayColorAlpha.value = forcedColor.alpha ?? 0.5
    return
  }

  let currentElement = carouselRoot.value
  while (currentElement) {
    const backgroundColor = window.getComputedStyle(currentElement).backgroundColor
    if (
      backgroundColor &&
      backgroundColor !== 'transparent' &&
      backgroundColor !== 'rgba(0, 0, 0, 0)'
    ) {
      const resolvedBackgroundColor = resolveColor(backgroundColor)
      if (resolvedBackgroundColor) {
        overlayColorRgb.value = resolvedBackgroundColor.rgb
        overlayColorAlpha.value = 0.5
        return
      }
    }
    currentElement = currentElement.parentElement
  }
}

/**
 * @description 开始观察所有子元素
 */
const observeChildren = () => {
  if (scrollContainer.value && resizeObserver) {
    Array.from(scrollContainer.value.children).forEach((child) => {
      resizeObserver.observe(child)
    })
  }
}

/**
 * @description 处理导航按钮滚动
 * @param {'left' | 'right'} direction
 */
const scroll = (direction) => {
  if (!scrollContainer.value) return
  if (direction === 'left' && !canScrollLeft.value) return
  if (direction === 'right' && !canScrollRight.value) return

  const containerWidth = scrollContainer.value.clientWidth
  const gapValue = effectiveGap.value
  const scrollAmount = containerWidth > 0 ? containerWidth : itemWidth.value + gapValue

  const target =
    direction === 'left'
      ? scrollContainer.value.scrollLeft - scrollAmount
      : scrollContainer.value.scrollLeft + scrollAmount

  const left =
    direction === 'left'
      ? Math.ceil((target + containerWidth) / (itemWidth.value + gapValue)) *
          (itemWidth.value + gapValue) -
        containerWidth
      : Math.floor(target / (itemWidth.value + gapValue)) * (itemWidth.value + gapValue)
  scrollContainer.value.scrollTo({
    left,
    behavior: 'smooth',
  })
}

/**
 * @description 滚动到指定索引的项，将其移动到中间位置
 * @param {number} index - 目标项的索引
 * @param {boolean} smooth - 是否使用平滑滚动，默认为true
 */
const scrollToItem = (index, smooth = true) => {
  if (!scrollContainer.value) return

  const children = Array.from(scrollContainer.value.children)
  if (index < 0 || index >= children.length) return

  const targetElement = children[index]
  if (!targetElement) return

  // 使用offsetLeft获取元素相对于父容器的位置，更准确且不受当前滚动影响
  const itemLeft = targetElement.offsetLeft
  const itemWidth = targetElement.offsetWidth
  const containerWidth = scrollContainer.value.clientWidth
  const itemCenter = itemLeft + itemWidth / 2
  const scrollLeft = itemCenter - containerWidth / 2

  // 限制滚动范围
  const maxScrollLeft = scrollContainer.value.scrollWidth - containerWidth
  const finalScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft))

  if (smooth) {
    scrollContainer.value.scrollTo({
      left: finalScrollLeft,
      behavior: 'smooth',
    })
  } else {
    scrollContainer.value.scrollLeft = finalScrollLeft
  }
}

/**
 * @description 根据滚动条滑块位置反向滚动卡片容器
 * @param {number} nextThumbLeft - 目标滑块左偏移
 * @returns {void}
 */
const scrollToThumbLeft = (nextThumbLeft) => {
  if (!scrollContainer.value || !scrollbarTrack.value) return

  const { scrollWidth, clientWidth } = scrollContainer.value
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
  const trackWidth = scrollbarTrack.value.clientWidth
  const maxThumbLeft = Math.max(0, trackWidth - scrollbarThumbPixelWidth.value)
  const limitedThumbLeft = Math.max(0, Math.min(nextThumbLeft, maxThumbLeft))

  scrollContainer.value.scrollLeft =
    maxThumbLeft > 0 ? (limitedThumbLeft / maxThumbLeft) * maxScrollLeft : 0
  updateCustomScrollbar()
}

/**
 * @description 停止拖拽自定义滚动条
 * @returns {void}
 */
const stopScrollbarDragging = () => {
  if (!isScrollbarDragging.value) return
  isScrollbarDragging.value = false

  if (import.meta.client) {
    window.removeEventListener('pointermove', handleScrollbarPointerMove)
    window.removeEventListener('pointerup', stopScrollbarDragging)
  }
}

/**
 * @description 拖拽自定义滚动条时同步商品横向滚动
 * @param {PointerEvent} event - 指针事件
 * @returns {void}
 */
const handleScrollbarPointerMove = (event) => {
  if (!isScrollbarDragging.value) return
  const deltaX = event.clientX - scrollbarDragStartX
  scrollToThumbLeft(scrollbarDragStartLeft + deltaX)
}

/**
 * @description 开始拖拽自定义滚动条滑块
 * @param {PointerEvent} event - 指针事件
 * @returns {void}
 */
const handleScrollbarThumbPointerDown = (event) => {
  if (!props.showScrollbar || !isOverflowing.value) return

  event.preventDefault()
  isScrollbarDragging.value = true
  scrollbarDragStartX = event.clientX
  scrollbarDragStartLeft = scrollbarThumbLeft.value

  if (import.meta.client) {
    window.addEventListener('pointermove', handleScrollbarPointerMove)
    window.addEventListener('pointerup', stopScrollbarDragging)
  }
}

/**
 * @description 点击滚动条轨道时跳转到对应商品位置并进入拖拽
 * @param {PointerEvent} event - 指针事件
 * @returns {void}
 */
const handleScrollbarTrackPointerDown = (event) => {
  if (!scrollbarTrack.value || !props.showScrollbar || !isOverflowing.value) return

  const rect = scrollbarTrack.value.getBoundingClientRect()
  const nextThumbLeft = event.clientX - rect.left - scrollbarThumbPixelWidth.value / 2
  scrollToThumbLeft(nextThumbLeft)
  handleScrollbarThumbPointerDown(event)
}

// 暴露方法供父组件调用
defineExpose({
  scrollToItem,
})

/**
 * @description 开始拖拽
 * @param {MouseEvent} e
 */
const startDragging = (e) => {
  isDragging.value = true
  startPageX = e.pageX
  startX.value = e.pageX - scrollContainer.value.offsetLeft
  scrollLeftState.value = scrollContainer.value.scrollLeft
  lastX.value = e.pageX
  velocity.value = 0

  if (rafId) cancelAnimationFrame(rafId)
}

/**
 * @description 停止拖拽
 */
const stopDragging = () => {
  if (!isDragging.value) return

  const didDrag = Math.abs(lastX.value - startPageX) >= DRAG_CLICK_THRESHOLD
  isDragging.value = false

  if (didDrag && import.meta.client) {
    const preventClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      document.removeEventListener('click', preventClick, true)
    }
    document.addEventListener('click', preventClick, true)
    setTimeout(() => {
      document.removeEventListener('click', preventClick, true)
    }, 0)
  }

  const applyMomentum = () => {
    if (Math.abs(velocity.value) > 0.5) {
      if (scrollContainer.value) {
        scrollContainer.value.scrollLeft -= velocity.value
        velocity.value *= 0.95
        rafId = requestAnimationFrame(applyMomentum)
      }
    }
  }
  rafId = requestAnimationFrame(applyMomentum)
}

/**
 * @description 拖拽中
 * @param {MouseEvent} e
 */
const onDragging = (e) => {
  if (!isDragging.value) return
  e.preventDefault()

  const x = e.pageX - scrollContainer.value.offsetLeft
  const walk = x - startX.value

  if (scrollContainer.value) {
    scrollContainer.value.scrollLeft = scrollLeftState.value - walk
    velocity.value = e.pageX - lastX.value
    lastX.value = e.pageX
  }
}

// 6. Lifecycle Hooks
onMounted(() => {
  nextTick(() => {
    updateCarouselState()
    syncOverlayColor()
  })

  if (import.meta.client) {
    window.addEventListener('resize', handleViewportResize, { passive: true })
  }

  // 监听容器及其子元素大小变化
  if (import.meta.client && window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      updateCarouselState()
    })

    if (scrollContainer.value) {
      resizeObserver.observe(scrollContainer.value)
      observeChildren()

      // 监听子元素的新增或移除
      mutationObserver = new MutationObserver(() => {
        updateCarouselState()
        observeChildren()
      })
      mutationObserver.observe(scrollContainer.value, { childList: true })
    }
  }
})

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId)
  if (edgeHoverResizeRafId) cancelAnimationFrame(edgeHoverResizeRafId)
  if (import.meta.client) {
    window.removeEventListener('resize', handleViewportResize)
  }
  stopScrollbarDragging()
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (mutationObserver) {
    mutationObserver.disconnect()
  }
})

// 7. Watchers
watch([() => props.background, () => props.overlayColor], () => {
  nextTick(() => {
    syncOverlayColor()
  })
})

watch(
  [
    isOverflowing,
    () => props.showScrollbar,
    () => props.alwaysShowScrollbar,
    () => props.scrollbarThumbWidth,
  ],
  () => {
    nextTick(() => {
      updateCustomScrollbar()
    })
  }
)
</script>

<template>
  <div
    ref="carouselRoot"
    class="base-carousel"
    :class="{
      'base-carousel--full-width': fullWidthScroll,
      'base-carousel--fade-left': isMobile && isOverflowing && canScrollLeft,
      'base-carousel--fade-right': isMobile && isOverflowing && canScrollRight,
    }"
    :style="{
      ...(background ? { backgroundColor: background } : {}),
      '--carousel-mask-color-rgb': overlayColorRgb,
      '--carousel-mask-color-alpha': overlayColorAlpha,
      '--carousel-edge-overlay-top': `${paddingTop}px`,
      '--carousel-edge-overlay-bottom': `${paddingBottom}px`,
      ...(overlayWidth !== '' && overlayWidth != null
        ? {
            '--carousel-edge-overlay-width':
              typeof overlayWidth === 'number' ? `${overlayWidth}px` : overlayWidth,
            '--carousel-edge-overlay-offset': '0px',
          }
        : {}),
    }"
    @mouseenter="showButtons = true"
    @mouseleave="showButtons = false"
  >
    <div
      ref="scrollContainer"
      class="base-carousel__grid"
      :class="[
        {
          'base-carousel__grid--dragging': isDragging,
          'base-carousel__grid--full-width': fullWidthScroll,
          'base-carousel__grid--center-when-fit': centerWhenNotOverflowing && !isOverflowing,
        },
      ]"
      :style="{
        gap: `${effectiveGap}px`,
        paddingTop: `${paddingTop}px`,
        paddingRight: fullWidthScroll ? undefined : `${paddingRight}px`,
        paddingBottom: `${paddingBottom}px`,
      }"
      @scroll="handleScroll"
      @mousedown="startDragging"
      @mousemove="onDragging"
      @mouseup="stopDragging"
      @mouseleave="stopDragging"
      @dragstart.prevent
    >
      <slot />
    </div>

    <div
      v-if="shouldExtendHoverArea"
      class="base-carousel__hover-zone base-carousel__hover-zone--left"
      :style="{ width: `${edgeHoverLeftWidth}px` }"
      aria-hidden="true"
    ></div>
    <div
      v-if="shouldExtendHoverArea"
      class="base-carousel__hover-zone base-carousel__hover-zone--right"
      :style="{ width: `${edgeHoverRightWidth}px` }"
      aria-hidden="true"
    ></div>

    <div
      v-if="shouldShowCustomScrollbar"
      ref="scrollbarTrack"
      class="base-carousel__scrollbar"
      :class="{ 'base-carousel__scrollbar--dragging': isScrollbarDragging }"
      :style="{ width: scrollbarWidth }"
      aria-hidden="true"
      @pointerdown="handleScrollbarTrackPointerDown"
    >
      <div
        class="base-carousel__scrollbar-thumb"
        :style="{
          width: `${scrollbarThumbPixelWidth}px`,
          transform: `translateX(${scrollbarThumbLeft}px)`,
        }"
        @pointerdown.stop="handleScrollbarThumbPointerDown"
      ></div>
    </div>

    <Transition name="fade">
      <div
        v-if="showNav && !isMobile && (showButtons || alwaysShowNav) && isOverflowing"
        class="base-carousel__nav"
        :class="{ 'base-carousel__nav--full-width': fullWidthScroll }"
      >
        <button
          v-show="canScrollLeft"
          :class="[
            'nav-btn nav-btn--left',
            `nav-btn--${size}`,
            { 'nav-btn--disabled': !canScrollLeft },
          ]"
          :style="navLeft != null ? { right: 'auto', left: `${navLeft}px` } : undefined"
          type="button"
          aria-label="Previous"
          :disabled="!canScrollLeft"
          @click.stop="canScrollLeft && scroll('left')"
        >
          <icon-left />
        </button>
        <button
          v-show="canScrollRight"
          :class="[
            'nav-btn nav-btn--right',
            `nav-btn--${size}`,
            { 'nav-btn--disabled': !canScrollRight },
          ]"
          :style="navRight != null ? { right: `${navRight}px`, left: 'auto' } : undefined"
          type="button"
          aria-label="Next"
          :disabled="!canScrollRight"
          @click.stop="canScrollRight && scroll('right')"
        >
          <icon-right />
        </button>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.base-carousel {
  --carousel-edge-overlay-offset: 1rem;
  --carousel-edge-overlay-width: #{$spacing-xl};
  --carousel-content-inline-offset: max(
    var(--ip-layout-page-padding),
    calc((100% - var(--ip-layout-container-width)) / 2)
  );
  position: relative;
  width: 100%;
  overflow: visible;

  @include respond-to-max('lg') {
    --carousel-edge-overlay-width: #{$spacing-md};
  }

  &::before,
  &::after {
    position: absolute;
    top: var(--carousel-edge-overlay-top, 0);
    bottom: var(--carousel-edge-overlay-bottom, 0);
    z-index: 2;
    width: calc(var(--carousel-edge-overlay-width) + var(--carousel-edge-overlay-offset));
    pointer-events: none;
    content: '';
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::before {
    left: calc(var(--carousel-edge-overlay-offset) * -1);
    background: linear-gradient(
      to right,
      rgba(var(--carousel-mask-color-rgb), var(--carousel-mask-color-alpha)),
      rgba(var(--carousel-mask-color-rgb), 0)
    );
  }

  &::after {
    right: calc(var(--carousel-edge-overlay-offset) * -1);
    background: linear-gradient(
      to left,
      rgba(var(--carousel-mask-color-rgb), var(--carousel-mask-color-alpha)),
      rgba(var(--carousel-mask-color-rgb), 0)
    );
  }

  &--fade-left::before,
  &--fade-right::after {
    opacity: 1;
  }

  &--full-width {
    --carousel-edge-overlay-offset: 0px;
    position: relative;
    width: 100%;
    max-width: none;
    overflow: visible;
  }

  &__grid {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0;
    cursor: grab;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-behavior: auto;
    user-select: none;
    -webkit-user-drag: none;
    user-drag: none;

    &::-webkit-scrollbar {
      display: none;
    }

    :deep(img),
    :deep(a) {
      -webkit-user-drag: none;
      user-drag: none;
    }

    @include respond-to-max('lg') {
      padding: 0;
    }

    &--dragging {
      cursor: grabbing;
      display: flex;
      overflow-x: auto;

      :deep(*) {
        transition: none !important;
      }
    }

    &--full-width {
      display: flex;
      overflow-x: auto;
      padding-left: var(--carousel-content-inline-offset);
      padding-right: var(--carousel-content-inline-offset);

      @include respond-to-max('lg') {
        padding: 0 20px;
      }
    }

    &--center-when-fit {
      justify-content: center;
    }
  }

  &__scrollbar {
    position: relative;
    width: 100%;
    height: 14px;
    display: flex;
    align-items: center;
    cursor: pointer;

    &::before {
      width: 100%;
      height: 1px;
      background-color: $border-dashed-color;
      content: '';
    }
  }

  &__hover-zone {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: auto;

    &--left {
      right: 100%;
    }

    &--right {
      left: 100%;
    }
  }

  &__scrollbar-thumb {
    position: absolute;
    left: 0;
    height: 1px;
    background-color: $primary-color;
    cursor: grab;
    transition: transform 0.15s ease;
    will-change: transform;
  }

  &__scrollbar--dragging {
    .base-carousel__scrollbar-thumb {
      cursor: grabbing;
      transition: none;
    }
  }

  &__nav {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: $z-index-carousel-navigation;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }

    @include respond-to-max('lg') {
      display: flex;
    }
  }

  .nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    border-radius: 50%;
    background-color: $base-carousel-nav-button-bg;
    color: $bg-white;
    backdrop-filter: blur($base-carousel-nav-button-backdrop-blur);
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color $base-carousel-nav-button-transition-duration
      $base-carousel-nav-button-transition-easing;
    box-shadow: none;

    &--default {
      --carousel-nav-button-current-size: #{$base-carousel-nav-button-size};
      width: var(--carousel-nav-button-current-size);
      height: var(--carousel-nav-button-current-size);

      :deep(.arco-icon) {
        font-size: $base-carousel-nav-button-icon-size;
      }
    }

    &--large {
      --carousel-nav-button-current-size: 64px;
      width: var(--carousel-nav-button-current-size);
      height: var(--carousel-nav-button-current-size);

      :deep(.arco-icon) {
        font-size: 24px;
      }
    }

    &--left {
      right: calc(100% + #{$spacing-xs});
      left: auto;

      :deep(.arco-icon) {
        transform: translateX(-$base-carousel-nav-button-icon-optical-offset);
      }
    }

    &--right {
      right: auto;
      left: calc(100% + #{$spacing-xs});

      :deep(.arco-icon) {
        transform: translateX($base-carousel-nav-button-icon-optical-offset);
      }
    }

    &:hover:not(:disabled):not(.nav-btn--disabled) {
      background-color: $base-carousel-nav-button-bg-hover;
      border-color: transparent;
      color: $bg-white;
      transform: translateY(-50%);
      box-shadow: none;
    }

    &:active:not(:disabled):not(.nav-btn--disabled) {
      background-color: $base-carousel-nav-button-bg-hover;
      transform: translateY(-50%);
    }

    &:disabled,
    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    :deep(.arco-icon) {
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &__nav--full-width {
    .nav-btn--left {
      right: auto;
      left: min(
        #{$base-carousel-full-width-nav-inset},
        calc(
          var(--carousel-content-inline-offset) -
            #{$spacing-xs} - var(--carousel-nav-button-current-size)
        )
      );
    }

    .nav-btn--right {
      right: min(
        #{$base-carousel-full-width-nav-inset},
        calc(
          var(--carousel-content-inline-offset) -
            #{$spacing-xs} - var(--carousel-nav-button-current-size)
        )
      );
      left: auto;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
